import { createFileRoute } from '@tanstack/react-router';
import SidebarApps from '@/components/layouts/navigation/SidebarApps';
import useAuthStore from '@/store/auth';
import useMapsStore from '@/store/maps';
import useVesselsStore from '@/store/vessels';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';

const MapsContainer = dynamic(() => import('@/featured/apps/components/maps/MapsContainer'), {
    ssr: false,
});
const MapsConfig = dynamic(() => import('@/featured/apps/components/maps/MapsConfig'), {
    ssr: false,
});

const MapsGLConfig = dynamic(() => import('@/featured/apps/components/maps-gl/MapsConfig'), {
    ssr: false,
});

const MapsGLLayer = dynamic(() => import('@/featured/apps/components/maps-gl/maps-layer'), {
    ssr: false,
});

const MapsGLToolbarSettings = dynamic(() => import('@/featured/apps/components/maps-gl/maps-toolbar-settings'), {
    ssr: false,
});

const MapsLayer = dynamic(
    () => import('@/featured/apps/components/maps/maps-layer'),
    { ssr: false }
);

const MapsVessels = dynamic(
    () => import('@/featured/apps/components/maps/MapsVessels'),
    { ssr: false }
);

const MapsMarker = dynamic(
    () => import('@/featured/apps/components/maps/maps-marker'),
    { ssr: false }
);

const MapsToolbarSettings = dynamic(
    () => import('@/featured/apps/components/maps/maps-toolbar-settings'),
    { ssr: false }
);

const NorificationWrapper = dynamic(
    () => import('@/featured/apps/components/modal/ModalOnloadMaps'),
    { ssr: false }
);

const MapsGLContainer = dynamic(
    () => import('@/featured/apps/components/maps-gl/MapsGLContainer'),
    { ssr: false }
);

import { isWebGLSupported } from '@/utils/hardwareDetection';
import useMapsGLStore from '@/store/maps-gl';
import { useRouter } from 'next/router';

const MapsGLVessels = dynamic(
    () => import('@/featured/apps/components/maps-gl/MapsGLVessels'),
    { ssr: false }
);

const MapsGLAIS = dynamic(
    () => import('@/featured/apps/components/maps-gl/MapsGLAIS'),
    { ssr: false }
);

const MapsGLMarker = dynamic(
    () => import('@/featured/apps/components/maps-gl/maps-marker'),
    { ssr: false }
);

export default function Apps() {
    const router = useRouter();
    const maps = useMapsStore((state) => state.maps);
    const isWindyMap = useMapsStore((state) => state.overlaysSettings.windyMap);
    const mapsGL = useMapsGLStore((state) => state.maps);

    const sidebar = useAuthStore((state) => state.sidebar);
    const mapMode = useAuthStore((state) => state.mapMode);
    const useWebGL = useAuthStore((state) => state.useWebGL);

    const isMapGL = (mapMode ? mapMode === 'maps-gl' : useWebGL) && isWebGLSupported();
    const shouldUseMapGL = !isWindyMap && isMapGL;

    const setSelectedVessel = useVesselsStore((state) => state.setSelectedVessel);
    const closeModalDetailMarker = useVesselsStore((state) => state.closeModalDetailMarker);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                if (localStorage.getItem('fms-theme') === 'weather') {
                    localStorage.removeItem('fms-theme');
                }
                if (localStorage.getItem('map_preference') === 'weather-map') {
                    localStorage.setItem('map_preference', 'basic-map');
                }
            } catch (e) {}
        }
    }, []);

    useEffect(() => {
        if (!router.isReady) return;
        const { provider, mode, ais } = router.query;
        if (provider === 'mst' || provider === 'stream' || provider === 'mt' || ais === 'true' || ais === '1') {
            useMapsStore.getState().setOverlaysSettings('marineTraffic', true);
            useMapsGLStore.getState().setOverlaysSettings('marineTraffic', true);
        }
        if (mode === 'webgl' || mode === 'maps-gl') {
            if (!isWindyMap) {
                useAuthStore.getState().setMapMode('maps-gl', false);
            }
        } else if (mode === 'leaflet_tiles' || mode === 'leaflet' || mode === 'maps-leaflet') {
            useAuthStore.getState().setMapMode('maps-leaflet', false);
        }
    }, [router.isReady, router.query, isWindyMap]);


    useEffect(() => {
        // Reset any active vessels and routes when switching map modes
        setSelectedVessel(null);
        closeModalDetailMarker();
    }, [shouldUseMapGL, setSelectedVessel, closeModalDetailMarker]);

    return (
        <>
            <div className="flex">
                <SidebarApps/>
                <NorificationWrapper/>

                <section className={clsx('relative h-screen w-screen', sidebar && 'hidden-sidebar')}>
                    {shouldUseMapGL ? (
                        <MapsGLContainer className="relative h-full w-full">
                            {mapsGL && (
                                <>
                                    <MapsGLVessels/>
                                    <MapsGLAIS/>
                                    <MapsGLLayer/>
                                    <MapsGLToolbarSettings/>
                                    <MapsGLConfig/>
                                </>
                            )}
                        </MapsGLContainer>
                    ) : (
                        <MapsContainer className="relative h-full w-full">
                            {maps && <MapsVessels/>}
                            <MapsLayer/>
                            <MapsToolbarSettings/>
                            {maps && <MapsConfig/>}
                        </MapsContainer>
                    )}

                    {shouldUseMapGL && mapsGL && <MapsGLMarker maps={mapsGL}/>}
                    {!shouldUseMapGL && maps && <MapsMarker maps={maps}/>}
                </section>
            </div>
        </>
    );
}

export const Route = createFileRoute('/_authenticated/apps')({
  component: Apps,
});
